<template>
  <div>
    <BaseDialog
      max-width="800"
      :persistent="isSubmitting"
      :value="dialogState"
      @click:outside="reset()"
      @keydown.esc="reset()"
    >
      <template #modal>
        <BaseCosmoCard>
          <template #title>
            <div class="modal_header">
              <BaseTitle v-text="$t('utils.relaunch')" />
              <BaseButtonIcon
                class="close_icon"
                icon="$mdiClose"
                @click="reset()"
              />
            </div>
          </template>
          <div v-if="cosmoform" class="modal_content">
            <div class="item_section mb-4">
              <div class="label" v-text="$tc('utils.recipient', 0)" />
              <div class="desc" v-text="cosmoform.email" />
            </div>
            <div class="item_section mb-4">
              <div class="label" v-text="'Demande de renseignement envoyé'" />
              <div class="desc" v-text="cosmoform.title" />
              <div class="item_footer" v-text="lastRemindDate" />
            </div>
            <div class="item_section mb-4">
              <div class="label" v-text="'Message de relance'" />
              <BaseMarkdownEditor
                v-model="message"
                :with-link="false"
                :error-messages="messageErrors"
              />
            </div>
          </div>
          <div class="modal_footer">
            <BaseButton
              :disabled="isSubmitting"
              color="primary"
              type="primary"
              @click="remindLogsDialogState = true"
            >
              {{ $t("utils.showLogs") }}
            </BaseButton>
            <BaseButton
              :disabled="isSubmitting"
              color="grey"
              type="secondary"
              @click="reset()"
            >
              {{ $t("utils.cancel") }}
            </BaseButton>
            <BaseButton
              :loading="isSubmitting"
              color="primary"
              type="primary"
              @click="relaunchInvitation()"
            >
              {{ $t("utils.relaunch") }}
            </BaseButton>
          </div>
        </BaseCosmoCard>
      </template>
    </BaseDialog>

    <RecordRelaunchLogsDialog
      :is-open="remindLogsDialogState"
      :all-records-logs="cosmoformLogs"
      @close="remindLogsDialogState = false"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import { postRelaunchCosmoform } from "./../Services/folder.service";
import RecordRelaunchLogsDialog from "./RecordRelaunchLogsDialog.vue";

import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";

export default {
  name: "RelaunchInvitationRecordDialog",
  components: { RecordRelaunchLogsDialog },
  mixins: [validationMixin],
  data() {
    return {
      remindLogsDialogState: false,
      message: null,
      isSubmitting: false,
    };
  },
  validations() {
    let validations = {};
    validations = {
      message: {
        required,
      },
    };
    return validations;
  },
  computed: {
    ...mapState({
      dialogState: (state) => state.folder.modals.isRelaunchCosmoformDialogOpen,
      cosmoform: (state) => state.folder.cosmoformToRelaunch,
    }),
    lastRemindDate() {
      return this.$t("utils.lastSentDate", {
        date: this.$d(
          new Date(this.cosmoform?.reminders[0]?.dateCreated),
          "veryshort"
        ),
      });
      // return null;
    },
    cosmoformLogs() {
      if (!this.cosmoform?.reminders?.length) return [];
      return this.cosmoform.reminders.map((reminder, index) => ({
        ...reminder,
        actionDisplay: this.$tc(
          "cosmoform.relaunchLogs",
          index === this.cosmoform.reminders.length - 1 ? 0 : 1,
          {
            clientEmail: this.cosmoform.email,
            cosmoformTitle: this.cosmoform.title,
            assignFromName:
              reminder.collaborator?.firstname !== "" &&
              reminder.collaborator?.lastname !== ""
                ? `${reminder.collaborator?.firstname} ${reminder.collaborator?.lastname}`
                : reminder.collaborator?.email,
          }
        ),
      }));
    },
    messageErrors() {
      if (!this.$v.message.$dirty) {
        return [];
      }
      if (!this.$v.message.required) {
        return [this.$t("utils.emailRequired")];
      }
      return [];
    },
  },
  methods: {
    reset() {
      this.$store.commit("folder/SET_RELAUNCH_COSMOFORM_DIALOG_OPEN", false);
      this.$store.commit("folder/SET_COSMOFORM_TO_RELAUNCH", null);
      this.message = null;
      this.isSubmitting = false;
    },
    async relaunchInvitation() {
      this.$v.message.$touch();
      if (this.$v.message.$invalid) return;
      this.isSubmitting = true;
      try {
        await postRelaunchCosmoform({
          organizationId: this.$route.params.organizationId,
          folderId: this.$route.params.folderId,
          cosmoformId: this.cosmoform.id,
          payload: {
            message: this.message,
          },
        });
        const response = await this.$store.dispatch(
          "folder/fetchActiveFolderCosmoformDetails",
          {
            organizationId: this.$route.params.organizationId,
            folderId: this.$route.params.folderId,
          }
        );
        this.$store.commit("folder/UPDATE_FOLDER_RECORDS_OFFER_COSMOFORM", {
          id: response[0]?.id,
          keyToUpdate: "reminders",
          value: [...response[0]?.reminders],
          type: "COSMOFORM",
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t("cosmoform.relaunchSuccess"),
          type: "SUCCESS",
        });
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("cosmoform.relaunchError"),
          type: "ERROR",
        });
      }
      this.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 8px;
  flex-wrap: wrap;
  .modal_title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #353542;
  }
  .close_icon {
    margin-left: auto;
  }
}
.modal_content {
  margin-top: 24px;
  .item_section {
    font-family: "Inter";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    .label {
      font-size: 12px;
      line-height: 16px;
      margin-bottom: 1em;
      color: #353542;
    }
    ::v-deep {
      .v-input__control .v-input__slot {
        & > fieldset {
          border: 1px solid #b6bdff;
        }
      }
    }
    .desc {
      padding: 16px;
      background: #f9f9f9;
      border-radius: 5px;
      line-height: 17px;
      color: #707080;
    }
    .files {
      padding: 16px;
      background: #f9f9f9;
      border-radius: 5px;
      line-height: 20px;
      color: #353542;
      text-decoration: underline;
    }
    .item_footer {
      margin-top: 10px;
      padding: 5px;
      font-weight: 500;
      line-height: 16px;
      text-align: right;
      color: #707080;
    }
  }
}
.modal_footer {
  display: flex;
  justify-content: flex-end;
  align-content: center;
  padding: 8px 0;
  & > * {
    &:first-child {
      margin: 0;
      margin-right: auto;
    }
    margin-left: 16px;
  }
}
</style>
